import React, { useRef, useEffect, useCallback, Fragment } from 'react';

import PropTypes from 'prop-types';

import cls from 'classnames';
import { l10n } from '@ott/l10n';
import styles from './OptionRenderer.scss';

const withComma = (element, condition) =>
  condition ? <Fragment>{element},&nbsp;</Fragment> : element;

const OptionRenderer = React.memo(
  ({
    option,
    isActive,
    onChange,
    OptionIconRenderer,
    CustomRenderer,
    shouldRenderAirportCity,
    shouldUseEllipsis,
    className,
    iconClassName,
  }) => {
    if (option.isGroup) {
      return (
        <div className={styles.groupContent}>
          {option.groupSlug || l10n('searchForm.groupSlugsMap', { slug: option.type })}
        </div>
      );
    }

    const optionRef = useRef(null);

    const onClick = useCallback(() => {
      onChange(option, { selectActive: true, selectNext: true });
    }, [optionRef, option, onChange]);

    if (!option) {
      return null;
    }

    if (CustomRenderer) {
      return (
        <div
          ref={optionRef}
          onClick={onClick}
          className={cls(styles.option, className, { [styles['option--isActive']]: isActive })}
          key={option.id}
        >
          <CustomRenderer {...option} />
        </div>
      );
    }

    const showIcon = Boolean(OptionIconRenderer);

    return (
      <div
        ref={optionRef}
        onClick={onClick}
        className={cls(styles.option, className, { [styles['option--isActive']]: isActive })}
      >
        {showIcon && (
          <div className={cls(styles.icon, iconClassName)}>
            <OptionIconRenderer option={option} />
          </div>
        )}
        <div className={styles.container}>
          <div className={styles.wrap}>
            <div
              className={cls(styles.text, {
                [styles.fullWidth]: !option.code,
                [styles.oneLine]: Boolean(shouldUseEllipsis),
              })}
            >
              <span className={styles.name}>{withComma(option.name, option.country)}</span>
              {option.city &&
                option.city !== option.name &&
                option.type === 'airport' &&
                shouldRenderAirportCity && (
                  <span className={styles.city}>
                    {option.city}
                    {option.country ? ',\u00A0' : ''}
                  </span>
                )}
              {option.country && <span className={styles.country}>{option.country}</span>}
            </div>
            {!!option.code && <span className={styles.extraData}>{option.code}</span>}
          </div>
          {!!option.description && <div className={styles.description}>{option.description}</div>}
        </div>
      </div>
    );
  }
);

OptionRenderer.displayName = 'OptionRenderer';

OptionRenderer.defaultProps = {};

OptionRenderer.propTypes = {
  OptionIconRenderer: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  option: PropTypes.object,
  isActive: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  shouldRenderAirportCity: PropTypes.bool,
  CustomRenderer: PropTypes.func,
};

export default OptionRenderer;
